// Responsive, pre-compressed hero artwork.
//
// The original hero photos were 2–14 MB each (up to 8000px wide) which made
// the slide background — the landing page LCP element — extremely slow.
// Each one is now shipped as WebP + JPEG at 640 / 1024 / 1600 / 1920 px so the
// browser downloads only what the viewport actually needs. Layout is
// unchanged: the same <img loading="eager" decoding="async"> box is filled, just with a lighter file.

import hero1w640 from "./optimized/hero1-640.webp";
import hero1w1024 from "./optimized/hero1-1024.webp";
import hero1w1600 from "./optimized/hero1-1600.webp";
import hero1j640 from "./optimized/hero1-640.jpg";
import hero1j1024 from "./optimized/hero1-1024.jpg";
import hero1j1600 from "./optimized/hero1-1600.jpg";

import hero2w640 from "./optimized/hero2-640.webp";
import hero2w1024 from "./optimized/hero2-1024.webp";
import hero2w1600 from "./optimized/hero2-1600.webp";
import hero2w1920 from "./optimized/hero2-1920.webp";
import hero2j640 from "./optimized/hero2-640.jpg";
import hero2j1024 from "./optimized/hero2-1024.jpg";
import hero2j1600 from "./optimized/hero2-1600.jpg";
import hero2j1920 from "./optimized/hero2-1920.jpg";

import hero3w640 from "./optimized/hero3-640.webp";
import hero3w1024 from "./optimized/hero3-1024.webp";
import hero3w1600 from "./optimized/hero3-1600.webp";
import hero3w1920 from "./optimized/hero3-1920.webp";
import hero3j640 from "./optimized/hero3-640.jpg";
import hero3j1024 from "./optimized/hero3-1024.jpg";
import hero3j1600 from "./optimized/hero3-1600.jpg";
import hero3j1920 from "./optimized/hero3-1920.jpg";

import hero1a640 from "./optimized/hero1-640.avif";
import hero1a1024 from "./optimized/hero1-1024.avif";
import hero1a1600 from "./optimized/hero1-1600.avif";
import hero2a640 from "./optimized/hero2-640.avif";
import hero2a1024 from "./optimized/hero2-1024.avif";
import hero2a1600 from "./optimized/hero2-1600.avif";
import hero2a1920 from "./optimized/hero2-1920.avif";
import hero3a640 from "./optimized/hero3-640.avif";
import hero3a1024 from "./optimized/hero3-1024.avif";
import hero3a1600 from "./optimized/hero3-1600.avif";
import hero3a1920 from "./optimized/hero3-1920.avif";

const srcset = (pairs) =>
  pairs.map(([url, width]) => `${url} ${width}w`).join(", ");

function makeSet({ avif, webp, jpeg, width, height }) {
  const fallback = jpeg[jpeg.length - 1][0];
  return {
    src: fallback,
    avifSrcSet: avif ? srcset(avif) : null,
    webpSrcSet: srcset(webp),
    jpegSrcSet: srcset(jpeg),
    width,
    height,
  };
}

// The hero box is 100vw, but on 3x phones "100vw" makes the browser pick the
// 1600/1920px candidate (~250 KB+) for a 390px screen, which is what pushed
// mobile LCP over budget. Capping the mobile hint to ~85vw keeps the 1024px
// variant — visually identical under object-fit: cover, far cheaper to fetch.
export const HERO_IMAGE_SIZES =
  "(max-width: 480px) 70vw, (max-width: 900px) 85vw, 100vw";

export const heroImageSets = [
  makeSet({
    avif: [
      [hero1a640, 640],
      [hero1a1024, 1024],
      [hero1a1600, 1600],
    ],
    webp: [
      [hero1w640, 640],
      [hero1w1024, 1024],
      [hero1w1600, 1600],
    ],
    jpeg: [
      [hero1j640, 640],
      [hero1j1024, 1024],
      [hero1j1600, 1600],
    ],
    width: 1717,
    height: 916,
  }),
  makeSet({
    avif: [
      [hero2a640, 640],
      [hero2a1024, 1024],
      [hero2a1600, 1600],
      [hero2a1920, 1920],
    ],
    webp: [
      [hero2w640, 640],
      [hero2w1024, 1024],
      [hero2w1600, 1600],
      [hero2w1920, 1920],
    ],
    jpeg: [
      [hero2j640, 640],
      [hero2j1024, 1024],
      [hero2j1600, 1600],
      [hero2j1920, 1920],
    ],
    width: 1920,
    height: 1280,
  }),
  makeSet({
    avif: [
      [hero3a640, 640],
      [hero3a1024, 1024],
      [hero3a1600, 1600],
      [hero3a1920, 1920],
    ],
    webp: [
      [hero3w640, 640],
      [hero3w1024, 1024],
      [hero3w1600, 1600],
      [hero3w1920, 1920],
    ],
    jpeg: [
      [hero3j640, 640],
      [hero3j1024, 1024],
      [hero3j1600, 1600],
      [hero3j1920, 1920],
    ],
    width: 1920,
    height: 1280,
  }),
];

// Poster used by the intro hero video — same artwork as slide 1 but served
// from the compressed variants instead of the 2.3 MB PNG.
export const heroPosterSet = heroImageSets[0];

// A <video poster> is a single URL, so it must match the preload hint exactly
// or the browser downloads two different files before the hero can paint.
// The 1024px WebP is the LCP candidate on mobile and desktop alike.
export const heroPosterUrl = hero1w1024;

export default heroImageSets;
